// Window Extensions

export interface WindowWithDataLayer extends Window {
  dataLayer?: Array<Record<string, string | number | boolean | object | undefined>>;
}

export type CustomAttributesRaw = Record<string, string | number | boolean | null | undefined>;

export interface AttributeContext {
  reason: string;
  conversationId: string | null;
  timestamp: number;
}

export type CustomAttributesInput =
  | CustomAttributesRaw
  | ((ctx: AttributeContext) => CustomAttributesRaw | Promise<CustomAttributesRaw>);

export interface ElisaChatClientConfig {
  deploymentId: string;
  deploymentEnv: string;
  region: string;
  deploymentName?: string;
  ui?: {
    branding?: {
      agentName?: string;
    };
  };
  customAttributes?: CustomAttributesInput;
  disableHostAuthProvider?: boolean;
  disableSessionProvider?: boolean;
  customSurveyUI?: boolean;
  downloadTranscript?: boolean;
  analyticsService?: string;
}

export interface ElisaChatClientDebugState {
  isReady: boolean;
  conversationId: string | null;
  hasActiveSession: boolean;
  historySource: string | null;
  authStatus: 'unknown' | 'started' | 'authenticated';
  authType: 'elisaIdClient' | 'oeExport' | 'guest';
  isSessionTimedOut: boolean;
  cookieConversationId: string | null;
  cookieDomain: string;
}

export interface ElisaChatClientApi {
  init: (config: ElisaChatClientConfig) => void;
  destroy: () => void;
  open: () => void;
  minimize: () => void;
  visible: (isVisible: boolean) => void;
  close: () => void;
  setAccessToken: (token: string) => void;
  getDebugState: () => ElisaChatClientDebugState;
  refreshCustomAttributes: () => Promise<void>;
  onSurveyOpen: (callback: (payload: { messageId: string; agentName?: string }) => void) => () => void;
  getSurveyPendingState: () => { messageId: string } | null;
  closeSurvey: () => Promise<void>;
  submitSurvey: (payload?: Record<string, unknown>) => Promise<void>;
  on: (callback: (event: ChatbotEvent) => void) => () => void;
}

export type GenesysMessageOriginatingEntity = 'Human' | 'Bot';

export interface GenesysMessageChannelFrom {
  nickname?: string;
  image?: string;
}

export interface GenesysMessagePayload {
  id: string;
  direction: 'Inbound' | 'Outbound';
  originatingEntity?: GenesysMessageOriginatingEntity;
  channel?: {
    from?: GenesysMessageChannelFrom;
  };
}

export interface MessageReceivedEvent {
  type: 'MESSAGE_RECEIVED';
  payload: GenesysMessagePayload;
}

export interface OtherChatbotEvent {
  type: string;
  payload?: unknown;
}

export type ChatbotEvent = MessageReceivedEvent | OtherChatbotEvent;

export const isMessageReceivedEvent = (event: ChatbotEvent): event is MessageReceivedEvent => {
  return event.type === 'MESSAGE_RECEIVED';
};

// Survey Types

export interface ChatFeedbackData {
  csat: number;
  care?: number;
  ces?: number;
  feedback: string;
  surveyMessageId?: string;
}

// Database Plugin Types

export enum GenesysService {
  NOE = 'noe',
  EOE = 'eoe',
  SALES = 'sales',
  OTHER = 'other',
}

export enum GenesysDeviceType {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export interface GenesysCustomAttributes {
  individualId?: string;
  accessToken?: string;
  chatStartUrl: string;
  service: GenesysService;
  deviceType: GenesysDeviceType;
  shoppingCart?: string;
  userLanguage?: string;
  firstName?: string;
  lastName?: string;
  chatToken?: string;
  email?: string;
  deploymentId?: string;
  deploymentName?: string;
}
